import { useState, useEffect } from 'react'
import apiService from '../services/api'
import AdminLayout from '../components/AdminLayout'
import toast from 'react-hot-toast'
import { Calendar, Save, Image } from 'lucide-react'
import { getImageUrl } from '../utils/apiUrl'

const AdminPopupEvent = () => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [popupData, setPopupData] = useState({
    enabled: false,
    title: '',
    message: '',
    startDate: '',
    endDate: '',
    link: '',
    linkText: '',
    image_path: '',
    delay_seconds: 0,
    display_frequency: 'once_per_session'
  })
  const [selectedFile, setSelectedFile] = useState(null)
  const [availableImages, setAvailableImages] = useState([])

  useEffect(() => {
    loadPopupData()
    fetchAvailableImages()
  }, [])

  const fetchAvailableImages = async () => {
    try {
      const response = await apiService.get('/upload/images?folder=popup-events')
      setAvailableImages(response.data.images || [])
    } catch (error) {
      console.error('Erreur chargement images:', error)
    }
  }

  const loadPopupData = async () => {
    try {
      setLoading(true)
      const response = await apiService.get('/popup-events')
      if (response.data && response.data.length > 0) {
        const event = response.data[0]
        setPopupData({
          enabled: event.enabled || false,
          title: event.title || '',
          message: event.message || '',
          startDate: event.start_date ? new Date(event.start_date).toISOString().slice(0, 16) : '',
          endDate: event.end_date ? new Date(event.end_date).toISOString().slice(0, 16) : '',
          link: event.link || '',
          linkText: event.link_text || '',
          image_path: event.image_path || '',
          delay_seconds: event.delay_seconds || 0,
          display_frequency: event.display_frequency || 'once_per_session'
        })
      } else {
        // Valeurs par défaut si aucun événement
        setPopupData({
          enabled: false,
          title: '',
          message: '',
          startDate: '',
          endDate: '',
          link: '',
          linkText: '',
          image_path: '',
          delay_seconds: 0,
          display_frequency: 'once_per_session'
        })
      }
    } catch (error) {
      console.error('Erreur chargement popup event:', error)
      // Si l'endpoint n'existe pas encore, utiliser les valeurs par défaut
      setPopupData({
        enabled: false,
        title: '',
        message: '',
        startDate: '',
        endDate: '',
        link: '',
        linkText: '',
        image_path: '',
        delay_seconds: 0,
        display_frequency: 'once_per_session'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setPopupData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Validation de la taille (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Le fichier est trop volumineux (max 5MB)')
      return
    }

    // Validation du type
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner un fichier image')
      return
    }

    setSelectedFile(file)
    toast.info('Image sélectionnée : ' + file.name)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validation
    if (popupData.enabled && (!popupData.title || !popupData.message)) {
      toast.error('Veuillez remplir le titre et le message si la popup est activée')
      return
    }
    
    if (popupData.enabled && (!popupData.startDate || !popupData.endDate)) {
      toast.error('Veuillez remplir les dates de début et de fin si la popup est activée')
      return
    }
    
    try {
      setSaving(true)
      
      // Si un fichier est sélectionné, l'uploader d'abord
      let imagePath = popupData.image_path
      if (selectedFile) {
        const reader = new FileReader()
        reader.onloadend = async () => {
          try {
            const response = await apiService.post('/upload/image', {
              image: reader.result,
              filename: selectedFile.name,
              folder: 'popup-events'
            })

            if (response.data && response.data.path) {
              imagePath = response.data.path
              await saveEventData(imagePath)
            }
          } catch (error) {
            console.error('Erreur upload image:', error)
            toast.error('Erreur lors de l\'upload de l\'image')
            setSaving(false)
          }
        }
        reader.readAsDataURL(selectedFile)
        return
      } else {
        await saveEventData(imagePath)
      }
    } catch (error) {
      console.error('Erreur sauvegarde popup event:', error)
      toast.error(error.response?.data?.error || 'Erreur lors de la sauvegarde')
      setSaving(false)
    }
  }

  const saveEventData = async (imagePath) => {
    try {
      const eventData = {
        enabled: popupData.enabled,
        title: popupData.title,
        message: popupData.message,
        start_date: popupData.startDate || null,
        end_date: popupData.endDate || null,
        link: popupData.link || null,
        link_text: popupData.linkText || null,
        image_path: imagePath || null,
        delay_seconds: parseInt(popupData.delay_seconds) || 0,
        display_frequency: popupData.display_frequency || 'once_per_session'
      }
      
      // Vérifier si un événement existe déjà
      const existing = await apiService.get('/popup-events')
      if (existing.data && existing.data.length > 0) {
        // Mettre à jour l'événement existant
        await apiService.put(`/popup-events/${existing.data[0].id}`, eventData)
        toast.success('Configuration de la popup événement mise à jour')
      } else {
        // Créer un nouvel événement
        await apiService.post('/popup-events', eventData)
        toast.success('Configuration de la popup événement créée')
      }
      
      setSelectedFile(null)
      await loadPopupData()
      await fetchAvailableImages()
    } catch (error) {
      console.error('Erreur sauvegarde popup event:', error)
      toast.error(error.response?.data?.error || 'Erreur lors de la sauvegarde')
      throw error
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <AdminLayout title="Administration" subtitle="PopUp Event">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout title="Administration" subtitle="PopUp Event">
      <div className="card">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Configuration de la PopUp Événement</h2>
          <p className="text-gray-600">Configurez la popup qui s'affichera sur le site pour un événement spécifique</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Activation */}
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="enabled"
              name="enabled"
              checked={popupData.enabled}
              onChange={handleInputChange}
              className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
            />
            <label htmlFor="enabled" className="text-sm font-medium text-gray-700">
              Activer la popup événement
            </label>
          </div>

          {/* Titre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Titre de la popup
            </label>
            <input
              type="text"
              name="title"
              value={popupData.title}
              onChange={handleInputChange}
              className="input-field"
              placeholder="Ex: Événement spécial"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message
            </label>
            <textarea
              name="message"
              value={popupData.message}
              onChange={handleInputChange}
              rows={4}
              className="input-field"
              placeholder="Message à afficher dans la popup"
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date de début
              </label>
              <input
                type="datetime-local"
                name="startDate"
                value={popupData.startDate}
                onChange={handleInputChange}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date de fin
              </label>
              <input
                type="datetime-local"
                name="endDate"
                value={popupData.endDate}
                onChange={handleInputChange}
                className="input-field"
              />
            </div>
          </div>

          {/* Lien */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lien (optionnel)
            </label>
            <input
              type="url"
              name="link"
              value={popupData.link}
              onChange={handleInputChange}
              className="input-field"
              placeholder="https://..."
            />
          </div>

          {/* Texte du lien */}
          {popupData.link && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Texte du lien
              </label>
              <input
                type="text"
                name="linkText"
                value={popupData.linkText}
                onChange={handleInputChange}
                className="input-field"
                placeholder="Ex: En savoir plus"
              />
            </div>
          )}

          {/* Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Image (optionnel)
            </label>
            
            {/* Upload d'image */}
            <div className="mb-3">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
              />
              <p className="text-xs text-gray-500 mt-1">
                {selectedFile ? `✅ Fichier sélectionné : ${selectedFile.name}` : 'Formats acceptés : JPG, PNG, WebP (max 5MB)'}
              </p>
              {selectedFile && (
                <p className="text-xs text-orange-600 mt-1">
                  💡 L'image sera uploadée lors de la sauvegarde
                </p>
              )}
            </div>

            <div className="mb-3 text-center text-sm text-gray-500">
              ou
            </div>

            {/* Sélecteur d'image */}
            <div className="mb-3">
              <select
                name="image_path"
                value={popupData.image_path}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="">Sélectionner une image existante</option>
                {availableImages.map((img) => (
                  <option key={img} value={img}>
                    {img.split('/').pop()}
                  </option>
                ))}
              </select>
            </div>

            {/* Aperçu de l'image */}
            {popupData.image_path && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Aperçu de l'image
                </label>
                <div className="relative inline-block">
                  <img
                    src={getImageUrl(popupData.image_path)}
                    alt="Aperçu popup"
                    className="max-w-full h-auto max-h-64 rounded-lg shadow-sm border border-gray-200"
                    onError={(e) => {
                      e.target.style.display = 'none'
                      if (e.target.nextSibling) {
                        e.target.nextSibling.style.display = 'flex'
                      }
                    }}
                  />
                  <div 
                    className="w-64 h-32 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center"
                    style={{ display: 'none' }}
                  >
                    <Image className="h-8 w-8 text-gray-400" />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setPopupData(prev => ({ ...prev, image_path: '' }))
                    setSelectedFile(null)
                  }}
                  className="mt-2 text-sm text-red-600 hover:text-red-800"
                >
                  Supprimer l'image
                </button>
              </div>
            )}
          </div>

          {/* Délai avant affichage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Délai avant affichage (secondes)
            </label>
            <input
              type="number"
              name="delay_seconds"
              value={popupData.delay_seconds}
              onChange={handleInputChange}
              min="0"
              max="300"
              className="input-field"
              placeholder="0"
            />
            <p className="text-xs text-gray-500 mt-1">
              Délai en secondes avant que la popup s'affiche (0 = immédiatement)
            </p>
          </div>

          {/* Fréquence d'affichage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fréquence d'affichage
            </label>
            <select
              name="display_frequency"
              value={popupData.display_frequency}
              onChange={handleInputChange}
              className="input-field"
            >
              <option value="once_per_session">Une fois par session</option>
              <option value="once_per_day">Une fois par jour</option>
              <option value="once_per_week">Une fois par semaine</option>
              <option value="every_time">À chaque visite</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Définit à quelle fréquence la popup s'affiche pour un même visiteur
            </p>
          </div>

          {/* Boutons */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t">
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex items-center space-x-2"
            >
              <Save className="h-4 w-4" />
              <span>{saving ? 'Sauvegarde...' : 'Sauvegarder'}</span>
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  )
}

export default AdminPopupEvent

